import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { API_CONFIG } from '../config/config';

const MFASetup = ({ onComplete, onCancel }) => {
  const { getToken } = useAuth();
  const [secret, setSecret] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSetup = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/mfa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to setup MFA');
      }

      const data = await response.json();
      setSecret(data.secret);
      setQrCode(data.qr_code);
      setSuccess('Einrichtung der MFA gestartet. Bitte scanne den QR-Code mit deiner Authentifikator-App.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/mfa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: verificationCode }),
      });

      if (!response.ok) {
        throw new Error('Invalid verification code');
      }

      const data = await response.json();
      onComplete(data.backup_codes);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Zwei-Faktor-Authentifizierung einrichten</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {!secret ? (
          <Box>
            <Typography variant="body1" gutterBottom>
              Klick auf den Button unten, um die Einrichtung zu starten.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSetup}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Start MFA Einrichtung'}
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" gutterBottom>
              Scanne diesen QR-Code mit deiner Authentifikator-App:
            </Typography>
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
              <QRCodeSVG value={qrCode} size={200} />
            </Box>
            <Typography variant="body1" gutterBottom>
              Oder gib diesen geheimen Schlüssel manuell ein:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
              {secret}
            </Typography>
            <TextField
              fullWidth
              label="Verifizierungscode"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              margin="normal"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleVerify}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Verifizieren'}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Abbrechen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MFASetup; 